import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import Button from 'carbon-react/lib/components/button';
import Icon from 'carbon-react/lib/components/icon';
import { RenderOpenProps } from 'carbon-react/lib/components/popover-container';

import { PopoverMenu } from '@futrli/ui-components/components/popover-menu/popover-menu';
import { usePopoverRowShield } from '@futrli/ui-components/hooks/usePopoverRowShield';

import { DriverDragHandlers } from '../../hooks/useDriversDragDrop/useDriversDragDrop';
import { NonFinancialDriverTreeNode } from '../../types';
import { useDriverActions } from '../context/driver-actions-context';

import * as styles from './drivers-data-grid.scss';

interface Props {
  row: NonFinancialDriverTreeNode;
  drag: DriverDragHandlers;
  index: number;
}

export const DriverRowActions = ({ row, drag, index }: Props) => {
  const { handleDeleteDriver, handleDuplicateDriver, handleEditDriver, handleExcelUpload } = useDriverActions();
  const attachShieldRef = usePopoverRowShield();
  const canMoveDown = drag.canMoveDown(index);
  const canMoveToBottom = drag.canMoveToBottom(index);
  const canMoveToTop = drag.canMoveToTop(index);
  const canMoveUp = drag.canMoveUp(index);
  const hasMoveActions = canMoveDown || canMoveToBottom || canMoveToTop || canMoveUp;

  const managementItems = row.is_excel_driver
    ? [
        {
          label: (
            <>
              <Icon className={styles.standardIconColor} type="file_excel" />
              <FormattedMessage id="common.excel_upload" defaultMessage="Excel upload" />
            </>
          ),
          onClick: handleExcelUpload,
          hasBorder: hasMoveActions
        }
      ]
    : [
        {
          label: <FormattedMessage id="common.edit" defaultMessage="Edit" />,
          onClick: () => handleEditDriver(row)
        },
        {
          label: <FormattedMessage id="common.duplicate" defaultMessage="Duplicate" />,
          onClick: () => handleDuplicateDriver(row)
        },
        {
          label: (
            <>
              <Icon type="delete" />
              <FormattedMessage id="common.delete" defaultMessage="Delete" />
            </>
          ),
          onClick: () => handleDeleteDriver(row),
          variant: 'danger' as const,
          hasBorder: hasMoveActions
        }
      ];

  const items = [
    ...managementItems,
    ...(canMoveUp
      ? [
          {
            label: <FormattedMessage id="common.move_up" defaultMessage="Move up" />,
            onClick: () => drag.onMoveUp(index)
          }
        ]
      : []),
    ...(canMoveDown
      ? [
          {
            label: <FormattedMessage id="common.move_down" defaultMessage="Move down" />,
            onClick: () => drag.onMoveDown(index)
          }
        ]
      : []),
    ...(canMoveToTop
      ? [
          {
            label: <FormattedMessage id="common.move_to_top" defaultMessage="Move to top" />,
            onClick: () => drag.onMoveToTop(index)
          }
        ]
      : []),
    ...(canMoveToBottom
      ? [
          {
            label: <FormattedMessage id="common.move_to_bottom" defaultMessage="Move to bottom" />,
            onClick: () => drag.onMoveToBottom(index)
          }
        ]
      : [])
  ];

  return (
    <div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} className={styles.darkFont}>
      <PopoverMenu
        items={items}
        renderOpenComponent={({ onClick, ref, 'aria-expanded': ariaExpanded }: RenderOpenProps) => (
          <Button
            buttonType="tertiary"
            iconType="ellipsis_vertical"
            iconPosition="after"
            onClick={onClick}
            ref={attachShieldRef(ref)}
            aria-expanded={ariaExpanded}
            size="small"
          >
            <FormattedMessage id="predict.actions" defaultMessage="Actions" />
          </Button>
        )}
      />
    </div>
  );
};
